<template>
  <div class="w-full">
    <div class="bg-white rounded-lg datatable-report w-full">
      <Datatable
        :id="id"
        ref="datatable"
        :map-row-class="mapRowClass"
        :use-datatable-export-csv="useDatatableExportCsv"
        :csv-file-name="title"
        :source="source"
        :config="config"
        :map-records="applyFilters"
        :filters="filters"
        :flat="flat"
        :download-url="downloadUrl"
        :no-download="noDownload"
        :bulk-select="bulkSelect"
        :bulk-select-key="bulkSelectKey"
        :bulk-select-ids="bulkSelectIds"
        :bulk-select-keep-alive="bulkSelectKeepAlive"
        :bulk-select-across-pages="bulkSelectAcrossPages"
        :scrollable="scrollable"
        :scrollable-height="scrollableHeight"
        :hide-global-filters="hideGlobalFilters"
        :hide-filters="hideFilters"
        :hide-page-length-select="hidePageLengthSelect"
        :default-page-length="defaultPageLength"
        :persist-search="persistSearch"
        :title="title"
        :hide-header="hideHeader"
        :auto-height="autoHeight"
        :subpage="subpage"
        :min-height="minHeight"
        :will-emit-download-report="hasDownloadReport"
        :reset-paging="resetPaging"
        :restore-page-on-return="restorePageOnReturn"
        v-bind="$attrs"
        @draw-complete="$emit('draw-complete', $event)"
        @download-report="handleDownloadReport"
        @filter-applied="$emit('filter-applied', $event)"
        @filter-removed="$emit('filter-removed', $event)"
        @filters-cleared="$emit('filters-cleared', $event)"
        @column-searched="$emit('column-searched', $event)"
      >
        <template #leftActions><slot name="leftActions"></slot></template>
        <template #rightActions><slot name="rightActions"></slot></template>
        <template v-for="(_, slotName) in $slots" #[slotName]="props">
          <slot :name="slotName" v-bind="props" />
        </template>
      </Datatable>
    </div>
  </div>
</template>

<script>
// Docs: app/javascript/components/DatatableReportV3.md
import { getQueryVariables } from "@/lib/url.js";

import Datatable from "@/components/DatatableV3.vue";

export default {
  components: {
    Datatable
  },
  props: {
    mapRowClass: {
      type: Function,
      default: null
    },
    scrollable: {
      type: Boolean,
      default: () => undefined
    },
    scrollableHeight: {
      type: String,
      default: () => undefined
    },
    bulkSelectIds: {
      type: Array,
      default: () => undefined
    },
    bulkSelectAcrossPages: {
      type: Boolean,
      default: () => undefined
    },
    bulkSelectKeepAlive: {
      type: Boolean,
      default: () => undefined
    },
    bulkSelect: {
      type: Boolean,
      default: () => undefined
    },
    bulkSelectKey: {
      type: String,
      default: () => undefined
    },
    useDatatableExportCsv: {
      type: Boolean,
      default: false
    },
    config: {
      // passed through to DatatableV3
      type: Object,
      default: null,
      required: true
    },
    filters: {
      type: Array,
      default: () => [],
      required: true
    },
    hideGlobalFilters: {
      // passed through to DatatableV3
      type: Boolean,
      default: false
    },
    hidePageLengthSelect: {
      // passed through to DatatableV3
      type: Boolean,
      default: false
    },
    defaultPageLength: {
      // passed through to DatatableV3
      type: Number,
      default: undefined
    },
    noDownload: {
      //passed through
      type: Boolean,
      default: false
    },
    source: {
      // passed through
      type: String,
      default: "",
      required: true
    },
    title: {
      // passed through
      type: String,
      default: ""
    },
    mapRecords: {
      // used in applyFilters
      type: Function,
      default: null
    },
    subpage: {
      // passed through
      type: Boolean,
      default: false
    },
    downloadUrl: {
      //used in exportReport
      type: String,
      default: ""
    },
    flat: {
      // Removes CSS card and box-shadow styling
      type: Boolean,
      default: false
    },
    persistSearch: {
      // Persists column searches across page loads
      type: Boolean,
      default: false
    },
    hideFilters: {
      type: Boolean,
      default: false
    },
    hideHeader: {
      type: Boolean,
      default: false
    },
    autoHeight: {
      type: Boolean,
      default: true
    },
    id: {
      type: String,
      default: undefined
    },
    minHeight: {
      type: Number,
      default: undefined
    },
    onDownloadReport: {
      type: Function
    },
    resetPaging: {
      type: Boolean,
      default: true
    },
    restorePageOnReturn: {
      type: Boolean,
      default: false
    }
  },
  emits: [
    "draw-complete",
    "download-report",
    "filter-applied",
    "filter-removed",
    "filters-cleared",
    "column-searched"
  ],
  computed: {
    flowbite() {
      return window.currentTenant.feature_flags.flowbite;
    }
  },
  methods: {
    applyFilters(rawRecord) {
      const record = this.mapRecords ? this.mapRecords(rawRecord) : rawRecord;
      if (this.filters) {
        // Apply datatable filters based on url params
        const params = getQueryVariables();
        this.filters.forEach((filter) => {
          const filterValue = params[this.getFilterParamName(filter)];
          if (filterValue) {
            record[filter.name] = filterValue;
          }
        });
      }
      return record;
    },
    getFilterParamName(filter) {
      return this.id ? `${this.id}_${filter.name}` : filter.name;
    },
    hasDownloadReport() {
      return Boolean(this.$props.onDownloadReport);
    },
    handleDownloadReport() {
      if (this.$props.onDownloadReport) {
        this.$props.onDownloadReport();
      } else {
        this.$emit("download-report");
      }
    },
    refreshTable(options) {
      if (this.$refs.datatable) {
        // Trigger datatable to self-update which will apply new filters
        this.$refs.datatable.refreshData(options);
      }
    },
    exportReport() {
      if (this.$refs.datatable) {
        this.$refs.datatable.exportReport();
      }
    }
  }
};
</script>

<style lang="scss"></style>
